import { useEffect } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

import { env } from "@/config/env";

setOptions({
  key: env.VITE_GOOGLE_MAPS_API_KEY,
  libraries: ["places"],
});

const CITY_COMPONENT_TYPES = [
  "locality",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_2",
  "neighborhood",
] as const;

const findComponent = (
  components: google.maps.GeocoderAddressComponent[],
  types: readonly string[],
  variant: "long_name" | "short_name" = "long_name",
) =>
  types.reduce<string>(
    (found, type) =>
      found ||
      (components.find((c) => c.types.includes(type))?.[variant] ?? ""),
    "",
  );

const fetchPlaceDetails = async (placeId: string) => {
  const results = await getGeocode({ placeId });
  return results[0]?.address_components ?? [];
};

export interface AddressComponents {
  address: string;
  city: string;
  zipCode: string;
  state: string;
}

const parseAddressComponents = (
  components: google.maps.GeocoderAddressComponent[],
): AddressComponents => {
  const streetNumber = findComponent(components, ["street_number"]);
  const route = findComponent(components, ["route"]);

  return {
    address: [streetNumber, route].filter(Boolean).join(" "),
    city: findComponent(components, CITY_COMPONENT_TYPES),
    zipCode: findComponent(components, ["postal_code"]),
    state: findComponent(
      components,
      ["administrative_area_level_1"],
      "short_name",
    ),
  };
};

export const useAddressAutocomplete = () => {
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    initOnMount: false,
    requestOptions: {
      componentRestrictions: { country: "us" },
      types: ["address"],
    },
    debounce: 300,
  });

  useEffect(() => {
    let cancelled = false;

    void importLibrary("places").then(() => {
      if (!cancelled) {
        init();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [init]);

  const handleSelect = async (placeId: string) => {
    clearSuggestions();
    const components = await fetchPlaceDetails(placeId);
    return parseAddressComponents(components);
  };

  return {
    ready,
    suggestions: data,
    hasSuggestions: status === "OK" && data.length > 0,
    onInputChange: setValue,
    onDismiss: clearSuggestions,
    onSelect: handleSelect,
  };
};
